import React, { useEffect, useRef } from 'react';

import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import OrderTransactionList from 'components/OrderTransactionList/OrderTransactionList';
import { Alert, Badge, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import GMetrixDialogPrompt from 'components/GMetrixDialogPrompt/GMetrixDialogPrompt';
import { LicenseInfoDialog } from 'components/LicenseInfoDialog/LicenseInfoDialog';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { GroupCodeWrapper } from 'components/GroupCodeWrapper/GroupCodeWrapper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useTransactionDetails } from 'pages/access_codes/context';
import { Transaction, TransactionApi } from 'apis/TransactionApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { FullfilledApiResponse } from 'apis/ApiCaller';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { useNotification } from 'app/notifications';
import { useNavigate } from 'react-router-dom';
import { useSuperState } from 'hooks';

interface AccessCodesDetailsProps {
    contentTiedToInactiveOrder: boolean;
    handleDuplicateTransaction: (dataItem: Transaction, setDuplicateInProcess?: any) => Promise<void>;
    tabNavigationClick: (value: number) => void;
}

export function AccessCodesDetails(props: AccessCodesDetailsProps) {

    const { contentTiedToInactiveOrder, handleDuplicateTransaction, tabNavigationClick } = props;

    const [state, setState] = useSuperState({
        duplicateInProcess: false,
        error: null as string,
        loading: false

    });
    const { duplicateInProcess, error, loading } = state;

    const { catchWithToast, createToast } = useNotification();
    const { pageContext, setTitle, update } = useTransactionDetails();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const gmetrixConfirmRef = useRef<any>();
    const gmetrixPromptRef = useRef<any>();
    const gmetrixDialogShellRef = useRef<any>();

    const setDuplicateInProcess = (duplicateInProcess: boolean) => setState({ duplicateInProcess });

    const clearError = () => setState({ error: null });

    const setArchiveStatus = async (id: number, archiveStatus: boolean) => {

        try {

            const result = await TransactionApi.SetArchivedStatus(id, archiveStatus);

            if (!result.Done) {

                createToast(result?.Message, 'danger');
                return;

            }

            const message = archiveStatus
                ? formatMessage({ id: 'access_code_was_archived_successfully.787657ea', defaultMessage: 'Access Code was archived successfully' })
                : formatMessage({ id: 'access_code_was_restored_successfully.526687d8', defaultMessage: 'Access Code was restored successfully' });

            createToast(message, 'success');

            if (pageContext?.Data.transactionDetails) {

                update({
                    transactionDetails: {
                        ...pageContext.Data.transactionDetails,
                        Archived: archiveStatus
                    },
                    transactionDetailsOriginal: {
                        ...pageContext.Data.transactionDetails,
                        Archived: archiveStatus
                    }
                });

            }

        } catch (e){

            createToast(e?.Message || e, 'danger');

        }

    };

    const handleEdit = async () => {

        const result = await gmetrixPromptRef.current.prompt('Edit Name',
            'Access Code Name',
            {
                buttonText: formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' }),
                hideCancel: false,
                variant: 'primary'
            },
            pageContext?.Data?.transactionDetails?.Name
        );

        if (result !== null && result.length > 0 && pageContext?.Data?.transactionDetails) {

            try {

                setState({
                    error: null,
                    loading: true
                });

                await TransactionApi.Update({ ...pageContext?.Data?.transactionDetails, Name: result }).catch(catchWithToast(formatMessage({ id: 'failed_to_update_access_code_name.cdf16274', defaultMessage: 'Failed to update access code name.' })));

                // save access code name
                update({
                    transactionDetails: {
                        ...pageContext?.Data?.transactionDetails,
                        Name: result
                    },
                    transactionDetailsOriginal: {
                        ...pageContext?.Data?.transactionDetails,
                        Name: result
                    }
                });

                setState({ loading: false });

                createToast(formatMessage({ id: 'access_code_edited_successfully.3aca8635', defaultMessage: 'Access code edited successfully' }), 'success');

            } catch (e){

                setState({
                    error: e?.message || e,
                    loading: false
                });

            }

        }

    };

    useEffect(() => {

        if (pageContext?.Id && pageContext?.Data.transactionDetails?.Name) {

            setTitle(pageContext?.Data.transactionDetails?.Name ?? '', false);

        }

    }, [pageContext?.Data.transactionDetails?.Name]);

    return (
        <>
            <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                {error}
            </Alert>

            {loading ?
                (
                    <div className='d-flex justify-content-center p-5'>
                        <GMetrixLoader />
                    </div>
                )
                :
                (
                    <>
                        {pageContext && pageContext.Data.transactionDetails && (
                            <Row className="mb-3">
                                <div className={`d-flex flex-column flex-lg-row justify-content-between gap-3 ${pageContext.Data.accessCodes && pageContext.Data.accessCodes?.length <= 1 && 'mb-2'}`}>
                                    <div className={'order-1 order-lg-0'}>
                                        {pageContext.Data.accessCodes && pageContext.Data.accessCodes?.length > 1 ?
                                            (
                                                <>
                                                    <div className="d-flex align-items-center">
                                                        <Form.Label className={`fw-bold`}>
                                                            <FormattedMessage defaultMessage="Code" id="code.cc651d9c" />
                                                        </Form.Label>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <GMetrixButton
                                                            className="p-0"
                                                            onClick={() => tabNavigationClick(3)}
                                                            size="sm"
                                                            variant={'link'}
                                                        >
                                                            <i className="far fa-sign-out text-primary me-2"></i>
                                                            <FormattedMessage defaultMessage="Go to Codes" id="go_to_codes.f5e995a9" />
                                                        </GMetrixButton>
                                                        {pageContext.Data.transactionDetails?.Archived && (
                                                        <h3 className="ms-2">
                                                            <Badge pill bg="warning">
                                                                <FormattedMessage defaultMessage="Archived" id="archived.e57c14b9" />
                                                            </Badge>
                                                        </h3>
                                                        )}
                                                    </div>
                                                </>
                                            )
                                            :
                                            (
                                                <GroupCodeWrapper
                                                    archived={pageContext.Data.transactionDetails?.Archived}
                                                    codeToCopy={pageContext.Data.accessCodes && pageContext.Data.accessCodes?.length > 0 ? pageContext.Data.accessCodes[0].AccessCodeTitle : ''}
                                                    label={formatMessage({ id: 'code.cc651d9c', defaultMessage: 'Code' })}
                                                />
                                            )
                                        }
                                    </div>

                                    <div className={`d-flex flex-wrap justify-content-lg-end gap-1 ${pageContext.Data.accessCodes && pageContext.Data.accessCodes?.length <= 1 ? 'mt-lg-4' : 'mt-lg-2'}`}>
                                        <GMetrixButton
                                            onClick={() => {

                                                pageContext && pageContext.Data.transactionDetails && handleDuplicateTransaction(pageContext.Data.transactionDetails, setDuplicateInProcess);
                                                setDuplicateInProcess(true);

                                            }}
                                            disabled={duplicateInProcess || contentTiedToInactiveOrder}
                                            iconClass="fa-solid fa-clone"
                                            variant="outline-primary"
                                        >
                                            <FormattedMessage defaultMessage="Duplicate" id="duplicate.9ae890dd" />
                                        </GMetrixButton>

                                        <GMetrixButton
                                            onClick={async () => {

                                                const confirmResult = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'convert_code_to_classroom.9cd65c8c', defaultMessage: 'Convert Code to Classroom' }),
                                                    <>
                                                        <h3>
                                                            {pageContext.Title}
                                                        </h3>
                                                        <p className="mb-2">
                                                            <FormattedMessage defaultMessage="This action will create a Classroom using the same settings, content, students, and reports. The original Access Code will be removed, but all of your data will remain available in the new Classroom." id="this_action_will_create_a_classroom_usin.86d247a8" />
                                                        </p>
                                                        <p>
                                                            <i className="mb-2">
                                                                <FormattedMessage defaultMessage="Note: Students with expired access will continue to have expired access in the new Classroom." id="note_students_with_expired_access_will_c.4eda541c" />
                                                            </i>
                                                        </p>
                                                        <p>
                                                            <FormattedMessage defaultMessage="This change cannot be undone. Proceed if you're ready to manage this group as a Classroom." id="this_change_cannot_be_undone_proceed_if.4b604782" />
                                                        </p>
                                                    </>,
                                                    { buttonText: formatMessage({ id: 'convert.8cfa9496', defaultMessage: 'Convert' }), variant: 'primary' });
                                                if (confirmResult) {

                                                    const transactionDetails = pageContext.Data.transactionDetails;

                                                    // If the transaction has no explicit ExpirationDate, set it to the order expiration date
                                                    // so the classroom inherits a valid end date
                                                    if (!transactionDetails.ExpirationDateString && transactionDetails.LeastOrderExpirationDate) {

                                                        try {

                                                            await TransactionApi.Update({
                                                                ...transactionDetails,
                                                                ExpirationDate: new Date(transactionDetails.LeastOrderExpirationDate),
                                                                ExpirationDateString: null,
                                                                StartDate: transactionDetails.StartDate
                                                                    ? new Date(transactionDetails.StartDate)
                                                                    : new Date()
                                                            } as any);

                                                        } catch {

                                                            // Non-blocking: the conversion can still proceed

                                                        }

                                                    }

                                                    // create the new classroom
                                                    let newClassroomId = 0;
                                                    const maxStudents = transactionDetails?.IndividualMaxStudents ?? 0;
                                                    const success: FullfilledApiResponse = await StudentGroupApi.CreateEditStudentGroup(0, pageContext.Title, `This classroom was converted from an access code: ${pageContext.Title}`, maxStudents, true, transactionDetails?.Identifier ?? pageContext.Title);

                                                    if (success.Done && success.ExtraData) {

                                                        newClassroomId = success.ExtraData;

                                                        try {

                                                            await TransactionApi.AddGroupToTransaction([pageContext.Id], [newClassroomId], transactionDetails?.PartnerId);

                                                        } catch {

                                                            await gmetrixDialogShellRef.current.create('Unexpected Error', <>
                                                                <p className="mb-2">
                                                                    <FormattedMessage defaultMessage="There has been an unexpected error. Your Access Code has not been converted to a Classroom, but no data has been lost." id="there_has_been_an_unexpected_error_your.d45e6bd9" />
                                                                    {' '}
                                                                </p>
                                                                <p>
                                                                    <FormattedMessage defaultMessage="Please try again in a moment." id="please_try_again_in_a_moment.782abe17" />
                                                                </p>
                                                            </>,
                                                            { buttonProps: { buttonText: formatMessage({ id: 'ok.70eb24fb', defaultMessage: 'OK' }), hideCancel: true, variant: 'danger' } });
                                                            return;

                                                        }

                                                    } else {

                                                        await gmetrixDialogShellRef.current.create('Unexpected Error', <>
                                                            <p className="mb-2">
                                                                <FormattedMessage defaultMessage="There has been an unexpected error. Your Access Code has not been converted to a Classroom, but no data has been lost." id="there_has_been_an_unexpected_error_your.d45e6bd9" />
                                                                {' '}
                                                            </p>
                                                            <p>
                                                                <FormattedMessage defaultMessage="Please try again in a moment." id="please_try_again_in_a_moment.782abe17" />
                                                            </p>
                                                        </>,
                                                        { buttonProps: { buttonText: formatMessage({ id: 'ok.70eb24fb', defaultMessage: 'OK' }), hideCancel: true, variant: 'danger' } });
                                                        return;

                                                    }

                                                    // get students and add them to the new classroom
                                                    const classroomObj = await StudentGroupApi.Get(newClassroomId);
                                                    const students = await TransactionApi.GetStudentsByTransactionId(pageContext.Id, 1, transactionDetails?.PartnerId ?? null).catch(catchWithToast(formatMessage({ id: 'failed_to_load_student_list.bb95052d', defaultMessage: 'Failed to load student list.' })));
                                                    const studentIds = students?.map(student => student.Id);
                                                    if (studentIds && studentIds?.length > 0) {

                                                        await StudentGroupApi.AddStudentToGroup([newClassroomId], studentIds).catch(catchWithToast(formatMessage({ id: 'failed_to_add_students_to_classroom.47feec92', defaultMessage: 'Failed to add students to classroom.' })));

                                                    }

                                                    // show modal once complete
                                                    await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'convert_code_to_classroom.9cd65c8c', defaultMessage: 'Convert Code to Classroom' }),
                                                        <>
                                                            <p className="mb-2">
                                                                <FormattedMessage defaultMessage="Conversion complete! All settings, content, students, and reports from the Access Code have been moved to this Classroom." id="conversion_complete_all_settings_content.4527842b" />
                                                            </p>
                                                            <p>
                                                                <b className="mb-2">
                                                                    <FormattedMessage defaultMessage="If anyone still has the old code but hasn't used it yet, be sure to share the new Classroom Code instead." id="if_anyone_still_has_the_old_code_but_has.82775e72" />
                                                                </b>
                                                            </p>
                                                            <GroupCodeWrapper codeToCopy={classroomObj.Control} label={formatMessage({ id: 'new_classroom_code.35139ae3', defaultMessage: 'New Classroom Code' })} />
                                                        </>,
                                                        { buttonText: formatMessage({ id: 'go_to_classroom.b5fbb38a', defaultMessage: 'Go To Classroom' }), hideCancel: true, variant: 'primary' });

                                                    // go to classroom
                                                    navigate(`/classrooms/${newClassroomId}`);

                                                }

                                            }}
                                            iconClass="fa-solid fa-undo"
                                            variant="outline-primary"
                                        >
                                            <FormattedMessage defaultMessage="Convert to Classroom" id="convert_to_classroom.f6b62dec" />
                                        </GMetrixButton>

                                        {pageContext.Data.transactionDetails.Archived ?
                                            (
                                                <GMetrixButton
                                                    onClick={async e => {

                                                        const dontRemindMeRestoreClassroom = sessionStorage.getItem('dontRemindMeRestoreClassroom');

                                                        if (dontRemindMeRestoreClassroom && JSON.parse(dontRemindMeRestoreClassroom)) {

                                                            // just restore the classroom without reminded modal
                                                            setArchiveStatus(pageContext.Id, false);
                                                            return;

                                                        }

                                                        const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'restore_access_code.b6da880b', defaultMessage: 'Restore Access Code' }),
                                                            <>
                                                                <h6>
                                                                    <FormattedMessage defaultMessage="Restoring an Access Code will:" id="restoring_an_access_code_will.dba85b09" />
                                                                </h6>
                                                                <ul>
                                                                    <li className="mb-2">
                                                                        <FormattedMessage defaultMessage="Restore students’ access to content assigned through the Access Code" id="restore_students_access_to_content_assig.0180ce10" />
                                                                    </li>
                                                                    <li className="mb-2">
                                                                        <FormattedMessage defaultMessage="Allow new student enrollments in this Access Code" id="allow_new_student_enrollments_in_this_ac.b718cf00" />
                                                                    </li>
                                                                    <li>
                                                                        <FormattedMessage defaultMessage="Restore the Access Code in report filters" id="restore_the_access_code_in_report_filter.d2f06020" />
                                                                    </li>
                                                                </ul>
                                                                <h6>
                                                                    <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                                </h6>

                                                            </>,
                                                            { buttonText: formatMessage({ id: 'restore_access_code.b6da880b', defaultMessage: 'Restore Access Code' }), iconClass: 'fa-regular fa-undo', variant: 'primary' },
                                                            { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                        );

                                                        if (success) {

                                                            //restore the classroom
                                                            setArchiveStatus(pageContext.Id, false);

                                                        }

                                                        // sets boolean value to block modal from appearing during the same session
                                                        if (dontRemindMe) {

                                                            sessionStorage.setItem('dontRemindMeRestoreClassroom', JSON.stringify(true));

                                                        }

                                                    }}
                                                    iconClass="fa-regular fa-undo"
                                                    size={'sm'}
                                                    variant="outline-primary"
                                                >
                                                    <FormattedMessage defaultMessage="Restore" id="restore.d528bbad" />
                                                </GMetrixButton>
                                            )
                                            :
                                            (
                                                <GMetrixButton
                                                    onClick={async e => {

                                                        const dontRemindMeArchiveAccessCode = sessionStorage.getItem('dontRemindMeArchiveAccessCode');

                                                        if (dontRemindMeArchiveAccessCode && JSON.parse(dontRemindMeArchiveAccessCode)) {

                                                            // just archive the classroom without reminded modal
                                                            setArchiveStatus(pageContext.Id, true);
                                                            return;

                                                        }

                                                        const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'archive_access_code.be9b846a', defaultMessage: 'Archive Access Code' }),
                                                            <>
                                                                <h6>
                                                                    <FormattedMessage defaultMessage="Archiving an Access Code will:" id="archiving_an_access_code_will.5789bf57" />
                                                                </h6>
                                                                <ul>
                                                                    <li className="mb-2">
                                                                        <FormattedMessage defaultMessage="Remove students’ access to content assigned through the Access Code" id="remove_students_access_to_content_assign.17c652fd" />
                                                                    </li>
                                                                    <li className="mb-2">
                                                                        <FormattedMessage defaultMessage="Prevent new student enrollments in this Access Code" id="prevent_new_student_enrollments_in_this.6a24f421" />
                                                                    </li>
                                                                    <li>
                                                                        <FormattedMessage defaultMessage="Hide the Access Code from report filters" id="hide_the_access_code_from_report_filters.9abac131" />
                                                                    </li>
                                                                </ul>
                                                                <h6>
                                                                    <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                                </h6>

                                                            </>,
                                                            { buttonText: formatMessage({ id: 'archive_access_code.be9b846a', defaultMessage: 'Archive Access Code' }), iconClass: 'fa-regular fa-cabinet-filing', variant: 'warning' },
                                                            { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                        );

                                                        if (success) {

                                                            //archive the classroom
                                                            setArchiveStatus(pageContext.Id, true);

                                                        }

                                                        // sets boolean value to block modal from appearing during the same session
                                                        if (dontRemindMe) {

                                                            sessionStorage.setItem('dontRemindMeArchiveAccessCode', JSON.stringify(true));

                                                        }

                                                    }}
                                                    iconClass="fa-regular fa-cabinet-filing"
                                                    variant="outline-primary"
                                                >
                                                    <FormattedMessage defaultMessage="Archive" id="archive.61ce1f3a" />
                                                </GMetrixButton>
                                            )
                                        }

                                    </div>
                                </div>

                                <Col md={4}>
                                    <div className="mt-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Access Code Name" id="access_code_name.35f7c8f3" />
                                        </strong>
                                        <br />
                                        {/* this needs to be changed */}
                                        {pageContext.Data.transactionDetails.Name !== '' ? pageContext.Data.transactionDetails.Name : formatMessage({ id: 'no_name.c0488f60', defaultMessage: 'No Name' })}
                                        <OverlayTrigger
                                            overlay={(
                                                <Tooltip>
                                                    <FormattedMessage defaultMessage="Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students." id="instructors_are_users_who_can_manage_cla.1dd0e940" />
                                                </Tooltip>
                                            )}
                                            placement="top"
                                        >
                                            <GMetrixButton
                                                className="ms-2 p-0"
                                                onClick={handleEdit}
                                                size="sm"
                                                variant="link"
                                            >
                                                <i className="fa-light fa-edit me-2"></i>
                                                <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                                            </GMetrixButton>

                                        </OverlayTrigger>
                                    </div>
                                    <div className="mt-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Available Uses" id="available_uses.28af4457" />
                                        </strong>

                                        <br />
                                        {pageContext.Data.transactionDetails.UsesRemaining}
                                        {' '}
                                        <FormattedMessage defaultMessage="Uses left" id="uses_left.791e0fea" />
                                        <OverlayTrigger
                                            overlay={(
                                                <Tooltip>
                                                    <FormattedMessage defaultMessage="Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students." id="instructors_are_users_who_can_manage_cla.1dd0e940" />
                                                </Tooltip>
                                            )}
                                            placement="top"
                                        >
                                            <GMetrixButton
                                                onClick={async () => {

                                                    await gmetrixDialogShellRef.current.create(formatMessage({ id: 'license_information.a6ad4cdd', defaultMessage: 'License Information' }),
                                                        <LicenseInfoDialog transactionDetails={pageContext.Data.transactionDetails} />,
                                                        { buttonProps: { buttonText: formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' }), hideCancel: true, variant: 'primary' }, size: 'xl' }
                                                    );

                                                }}
                                                className="ms-2 p-0"
                                                size="sm"
                                                variant="link"
                                            >
                                                <i className="fa-light fa-circle-info me-2"></i>
                                                <FormattedMessage defaultMessage="View Details" id="view_details.fa7bb04a" />
                                            </GMetrixButton>

                                        </OverlayTrigger>
                                    </div>
                                    {pageContext.Data.licenseHistory && pageContext.Data.transactionDetails.OrderIds && (
                                        <OrderTransactionList
                                            licenseHistory={pageContext.Data.licenseHistory}
                                            orderIds={pageContext.Data.transactionDetails.OrderIds}
                                        />
                                    )}
                                </Col>

                                <Col md={4}>
                                    <div className="mt-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Identifier" id="identifier.45eac7f9" />
                                        </strong>
                                        <br />
                                        {pageContext.Data.transactionDetails.Identifier !== '' ? pageContext.Data.transactionDetails.Identifier : '------'}
                                    </div>
                                    <div className="mt-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Date Created" id="date_created.85efe6f3" />
                                        </strong>
                                        <br />
                                        {pageContext.Data.transactionDetails.CreatedDate.toString().substring(0, pageContext.Data.transactionDetails.CreatedDate.toString().indexOf('T'))}
                                    </div>
                                    <div className="mt-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Created By" id="created_by.a458757d" />
                                        </strong>
                                        <br />
                                        {pageContext.Data.transactionDetails.CreatedBy}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                )
            }

            <GMetrixDialogConfirm ref={gmetrixConfirmRef} />
            <GMetrixDialogPrompt ref={gmetrixPromptRef} />
            <GMetrixDialogShell ref={gmetrixDialogShellRef} />
        </>
    );

}
